import { useEffect, useState, type FormEvent } from "react";
import { api } from "@/lib/apiClient";
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
} from "@/lib/supportTickets";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { TicketCategory, TicketCreateBody, TicketDetail, TicketPriority } from "@/types/supportTicket";

export function CreateTicketModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (ticket: TicketDetail) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<TicketCategory>("sonstiges");
  const [priority, setPriority] = useState<TicketPriority>("normal");
  const [locationHint, setLocationHint] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setCategory("sonstiges");
    setPriority("normal");
    setLocationHint("");
    setError(null);
    setSaving(false);
  }, [open]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (trimmedTitle.length < 3) {
      setError("Der Titel muss mindestens 3 Zeichen haben.");
      return;
    }
    if (!trimmedDescription) {
      setError("Bitte eine Beschreibung angeben.");
      return;
    }
    setSaving(true);
    setError(null);
    const body: TicketCreateBody = {
      title: trimmedTitle,
      description: trimmedDescription,
      category,
      priority,
      location_hint: locationHint.trim() || null,
    };
    try {
      const created = await api.post<TicketDetail>("/api/v1/tickets", body);
      onCreated(created);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Ticket konnte nicht angelegt werden.");
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Neues Ticket"
      description="Die Gruppe Camping-Admin sieht das Ticket und bearbeitet es."
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button type="submit" form="create-ticket-form" loading={saving}>
            Ticket anlegen
          </Button>
        </div>
      }
    >
      <form id="create-ticket-form" className="space-y-4" onSubmit={onSubmit}>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Titel</span>
          <input
            className="eh-input w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            autoFocus
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Kategorie</span>
            <select
              className="eh-input w-full"
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
            >
              {TICKET_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {TICKET_CATEGORY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Priorität</span>
            <select
              className="eh-input w-full"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              {TICKET_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {TICKET_PRIORITY_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Beschreibung</span>
          <textarea
            className="eh-input min-h-32 w-full resize-y"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={8000}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-label-secondary">Bezug (optional)</span>
          <input
            className="eh-input w-full"
            value={locationHint}
            onChange={(e) => setLocationHint(e.target.value)}
            maxLength={200}
            placeholder="z. B. Platz 12, Sanitärhaus 2, Kasse"
          />
        </label>
      </form>
    </Modal>
  );
}
